import React, { useEffect, useState } from "react";
import axios from "axios";
import { Play, Clock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  All: "all",
  Menstruation: "menstruation",
  Safety: "safety",
  "Well-being": "wellbeing",
};

export default function Education() {
  const { t, lang } = useLang();
  const [videos, setVideos] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/videos?language=${lang}`)
      .then((r) => setVideos(r.data))
      .catch(() => setVideos([]));
  }, [lang]);

  const filtered = filter === "All" ? videos : videos.filter((v) => v.category === filter);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
          {t("education_hub")}
        </h1>
        <p className="mt-2 sm:mt-3 text-base sm:text-lg text-muted-foreground max-w-2xl">{t("education_desc")}</p>
      </div>

      <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8" data-testid="video-filters">
        {Object.keys(CATEGORY_LABELS).map((cat) => (
          <button
            key={cat}
            data-testid={`filter-${CATEGORY_LABELS[cat]}`}
            onClick={() => setFilter(cat)}
            className={`px-4 sm:px-5 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base border-2 transition-colors ${
              filter === cat
                ? "bg-primary text-white border-primary"
                : "bg-white text-foreground border-border hover:border-primary"
            }`}
          >
            {t(CATEGORY_LABELS[cat]) || cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filtered.map((v, i) => (
          <button
            key={v.id}
            data-testid={`video-card-${v.id}`}
            onClick={() => setActive(v)}
            className="text-left card-lift bg-white rounded-3xl overflow-hidden border border-border fade-up"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="relative aspect-video bg-foreground/10">
              <img
                src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`}
                alt={v.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
                  <Play className="w-8 h-8 text-primary fill-primary translate-x-0.5" strokeWidth={2} />
                </div>
              </div>
              <span className="absolute top-3 left-3 text-xs font-bold uppercase tracking-wider bg-white/95 text-foreground rounded-full px-3 py-1">
                {v.category}
              </span>
            </div>
            <div className="p-5">
              <h3 className="text-lg font-bold text-foreground leading-snug">{v.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{v.description}</p>
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5" /> {v.duration} • Age {v.age}
              </div>
            </div>
          </button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-full p-0 bg-white overflow-hidden max-h-[92vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader className="p-5 border-b border-border">
                <DialogTitle className="text-xl font-bold">{active.title}</DialogTitle>
                <DialogDescription className="sr-only">{active.description}</DialogDescription>
              </DialogHeader>
              <div className="aspect-video bg-black">
                <iframe
                  data-testid="video-player"
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1&rel=0`}
                  title={active.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-5 text-sm text-muted-foreground">{active.description}</div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
